import {Component, ElementRef, EventEmitter, HostListener, inject, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Router, RouterLink} from "@angular/router";
import {AuthService} from "../../services/auth/auth.service";
import {UserDto} from "../../repositories/dto/user.dto";

import {TranslatePipe} from "@ngx-translate/core";
import {LogoComponent} from "../logo/logo.component";
import {IconComponent} from "../../libs/icon/icon.component";
import {Paths} from "../../paths";
import {UserService} from "../../services/user.service";
import {FormService} from "../../services/form.service";
import {isDefined, isFalse, nonBlank, unsubscribe} from "../../libs/utils/epic.utils";
import {NgbPopover} from "@ng-bootstrap/ng-bootstrap";
import {I18nService} from "../../services/i18n/i18n.service";
import {delay, Subscription} from "rxjs";
import {StateService} from "../../services/state/state.service";
import {AccStateEnum} from "../../services/state/acc-state.enum";
import {NotificationService} from "../../services/notification/notification.service";
import {LoggingService} from "../../services/logger/logging.service";
import {FormStateEnum} from "../../libs/form/items/form-state.enum";
import {OrgUserDto} from "../../repositories/dto/org-user.dto";
import {OrgDto} from "../../repositories/dto/org.dto";
import {NotificationsItem} from "../../services/notification/notifications.item";
import {NotificationDto} from "../../repositories/dto/notification.dto";
import {PermissionService} from "../../services/auth/permission.service";
import {Permissions} from "../../services/auth/permissions.item";
import {FaIconComponent, IconDefinition} from "@fortawesome/angular-fontawesome";
import {faAnglesRight} from "@fortawesome/free-solid-svg-icons/faAnglesRight";
import {environment} from "../../../environments/environment";

@Component({
  selector: 'acc-header',
  imports: [
    RouterLink,
    TranslatePipe,
    LogoComponent,
    IconComponent,
    NgbPopover,
    FaIconComponent
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {

  @Output()
  public readonly accToggleSidebarState: EventEmitter<any> = new EventEmitter<any>();

  protected readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  protected readonly supportListEnabled: boolean = environment.support.enabled && environment.support.list;
  protected readonly Paths: typeof Paths = Paths;
  protected readonly sidebarIcon: IconDefinition = faAnglesRight;
  protected user: UserDto | undefined;
  protected show: 'user' | 'org' | 'notification' | undefined = undefined;
  protected showFormStatus: boolean = false;
  protected formStatus: string = '';
  protected formStatusText: string = '';
  protected isStateOrg: boolean = false;
  protected nfs: NotificationsItem | undefined;
  protected org: OrgDto | undefined;

  private readonly auth: AuthService = inject(AuthService);
  private readonly userService: UserService = inject(UserService);
  private readonly formService: FormService = inject(FormService);
  private readonly i18n: I18nService = inject(I18nService);
  private readonly router: Router = inject(Router);
  private readonly stateService: StateService = inject(StateService);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  private readonly subs: Subscription[] = [];
  private assignedOrgs: OrgUserDto[] | undefined = undefined;
  private perms: Permissions;

  constructor() {
    this.perms = this.permissionService.getPerms();
  }

  ngOnInit(): void {
    this.subs.push(
      this.userService.getUser().subscribe((user) => this.user = user),
      this.userService.getAssignedOrgs().subscribe((orgs) => this.assignedOrgs = orgs),
      // pipe(delay(0)) - ExpressionChangedAfterItHasBeenCheckedError
      this.formService.onStateChange().pipe(delay(0)).subscribe((status) => this.setFormStatus(status)),
      this.stateService.getState().subscribe((state) => this.isStateOrg = state === AccStateEnum.ORG),
      this.stateService.getOrg().subscribe((o) => this.org = o),
      this.notificationService.getNotifications().subscribe((n) => this.nfs = n),
      this.permissionService.accChanged().subscribe(((p) => this.perms = p)),
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected showNone(): void {
    this.show = undefined;
  }

  protected showOrg(): void {
    this.show = 'org';
  }

  protected isShowOrg(): boolean {
    return this.show === 'org';
  }

  protected showUser(): void {
    this.show = 'user';
  }

  protected isShowUser(): boolean {
    return this.show === 'user';
  }

  protected showNotification(): void {
    this.show = 'notification';
  }

  protected isShowNotification(): boolean {
    return this.show === 'notification';
  }

  // No data-bs-toggle: Bootstrap would close on click what hovering just opened
  protected openNotification(event: Event): void {
    event.preventDefault();
    this.showNotification();
  }

  protected signout(): void {
    this.auth.logOut();
  }

  // First redirect, then change org or user; otherwise an attempt will be made to load an invoice with the wrong org-id
  protected routeOrgList(): void {
    this.router.navigate([Paths.ORG_LIST]).then(() => {
      this.stateService.useUser().then();
    });
  }

  // First redirect, then change org or user; otherwise an attempt will be made to load an invoice with the wrong org-id
  protected useOrg(id: string): void {
    this.router.navigate([Paths.OVERVIEW]).then(() => {
      this.stateService.useOrg(id).then();
    });
  }

  // First redirect, then change org or user; otherwise an attempt will be made to load an invoice with the wrong org-id
  protected useUser(): void {
    this.router.navigate([Paths.OVERVIEW]).then(() => {
      this.stateService.useUser().then();
    });
  }

  protected toggleSidebarState(): void {
    this.accToggleSidebarState.emit('');
  }

  // Close the mobile burger menu (Bootstrap collapse) when clicking outside of it.
  // Re-using the toggler's click lets Bootstrap handle the hide animation and keep its state in sync.
  @HostListener('document:click', ['$event'])
  protected closeBurgerOnOutsideClick(event: MouseEvent): void {
    const collapse: HTMLElement | null = this.host.nativeElement.querySelector('#navbarSupportedContent');
    const toggler: HTMLElement | null = this.host.nativeElement.querySelector('.navbar-toggler');
    if (isDefined(collapse) && collapse.classList.contains('show')) {
      const target: Node = event.target as Node;
      const clickInsideMenu: boolean = collapse.contains(target);
      const clickOnToggler: boolean = isDefined(toggler) && toggler.contains(target);
      if (isFalse(clickInsideMenu) && isFalse(clickOnToggler)) {
        toggler?.click();
      }
    }
  }

  protected getActiveOrgs(): OrgDto[] {
    const orgUsers: OrgUserDto[] = this.assignedOrgs ? this.assignedOrgs : [];
    // isAllowSwitchToOrg() also covers org.enabled, org.hasPermissions() and the confirmed state. Without the
    // permission check the switcher would offer orgs that answer 403 on every request - the org list hides those
    // the same way (see org-list.component.html)
    return orgUsers.filter((ou) => ou.isEnabled() && ou.isAllowSwitchToOrg())
      .map((ou) => ou.org)
      .filter((o) => isDefined(o))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  protected notifyClick(notification: NotificationDto): void {
    this.markAsRead(notification);
    const type: string = notification.type;
    if (type === 'ORG_ADDRESS_NOT_EXISTS' && this.perms.settingsOrg.read) {
      this.router.navigate([Paths.SETTINGS_ORG]).then();
    } else if ((type === 'FEATURES_EXPIRE_ONE_WEEK' || type === 'FEATURES_EXPIRE_TWO_WEEKS')
      && this.perms.orgWallet.read) {
      this.router.navigate([Paths.ORG_WALLET_TRANSACTION_LIST]).then();
    } else if (type === 'FEATURES_TRIAL_EXPIRE_ONE_WEEK' && this.perms.orgWallet.read) {
      this.router.navigate([Paths.FEATURE_LIST]).then();
    } else if (type === 'SUPPORT_TICKET_ANSWERED') {
      if (this.supportListEnabled) {
        this.router.navigate([Paths.SUPPORT_LIST]).then();
      }
    } else if (nonBlank(type)) {
      this.log.warn('Notification navigate not set for type "{}"', [type]);
    }
  }

  private setFormStatus(status: string): void {
    this.formStatus = status;
    this.showFormStatus = nonBlank(status);
    if (this.showFormStatus) {
      this.i18n.translateAsync('form.status.' + status).then((t) => {
        this.formStatusText = t;
        this.appendDebugFormStatusText();
      });
    } else {
      this.formStatusText = '';
      this.appendDebugFormStatusText();
    }
  }

  private appendDebugFormStatusText(): void {
    if (this.log.isDebugEnabled()) {
      this.formStatusText += '\n\nActive form wrappers:';
      this.formService.getWrapperMap().forEach((value, id) => {
        this.formStatusText += '\n' + id + " - " + FormStateEnum[value.wrapper.getState()];
      });
    }
  }

  private markAsRead(notification: NotificationDto): void {
    this.notificationService.markAsRead(notification);
    const notify: NotificationDto | undefined = this.nfs?.notifications.find((n) => n.id === notification.id);
    if (isDefined(notify)) {
      notify.read = true;
    }
  }
}
